// Render a panel of buttons to perform actions over EditDispatchScheduleContext
import React from 'react';

import { ArrowUturnLeftIcon } from '@heroicons/react/16/solid';
import { useToggle } from '@uidotdev/usehooks';
import clsx from 'clsx';

import { find } from 'lodash';

import AssignmentsConflictsPanel from '@/components/Job/JobsList/AssignmentsConflictsPanel';
import Confirm from '@/components/Modal/Confirm';

import { useEditSchedulerContext } from '@/context/EditSchedulerContext';

import { JobDoc } from '@isd/lib/firebase/db/metaTypes';

export default function EditDispatchScheduleActionButtons({
  jobs,
}: {
  jobs: JobDoc[];
}) {
  const {
    onConfirmAssignmentChanges,
    isLoading,
    selectedGroup,
    onRevertAssignmentChanges,
    isCheckingConflicts,
  } = useEditSchedulerContext();
  const [isModalOpen, toggleIsModalOpen] = useToggle();
  const [isRevertModalOpen, toggleIsRevertModalOpen] = useToggle();

  const saveAndClose = (toggle: () => void) =>
    onConfirmAssignmentChanges().then(() => toggle());

  const onConfirmSave = () => {
    saveAndClose(toggleIsModalOpen);
  };

  const onConfirmRevert = () => {
    onRevertAssignmentChanges();
    toggleIsRevertModalOpen();
  };

  return (
    <>
      <div
        className={clsx(
          'action-btn-panel bg-base-150 absolute top-[5%] z-10 flex gap-1 rounded-lg px-3 py-1 shadow-xl',
          selectedGroup === null ? 'left-[5%]' : 'left-[26%]'
        )}
      >
        <ConfirmChangesButton onClick={() => toggleIsModalOpen()} />
        <RevertChangesButton onClick={() => toggleIsRevertModalOpen()} />
      </div>
      <Confirm
        isOpen={isModalOpen}
        isLoading={isLoading}
        isConfirmDisabled={isCheckingConflicts}
        content={<ConfirmSaveChangesModalContent jobs={jobs} />}
        onConfirm={onConfirmSave}
        onCancel={toggleIsModalOpen}
      />
      <Confirm
        isOpen={isRevertModalOpen}
        isLoading={isLoading}
        content={<ConfirmRevertChangesContent />}
        confirmBtnLabel="Discard Changes"
        onConfirm={onConfirmRevert}
        onCancel={toggleIsRevertModalOpen}
      />
    </>
  );
}

function ConfirmChangesButton({
  onClick,
}: {
  onClick: React.MouseEventHandler;
}) {
  const { operationsRecordList, isConfirmChangesDisabled } =
    useEditSchedulerContext();
  return (
    <div
      className={clsx(
        'confirm-changes-btn btn btn-primary btn-sm text-sm shadow-xs',
        (!operationsRecordList.length || isConfirmChangesDisabled) &&
          'btn-disabled cursor-not-allowed!'
      )}
      onClick={onClick}
    >
      {'Confirm Changes '}
      <span className="text-xl">
        <SaveIcon />
      </span>
    </div>
  );
}

function RevertChangesButton({
  onClick,
}: {
  onClick: React.MouseEventHandler;
}) {
  const { operationsRecordList } = useEditSchedulerContext();
  return (
    <div
      className={clsx(
        'discard-changes-btn btn btn-sm shadow-xs',
        !operationsRecordList.length && 'btn-disabled'
      )}
      onClick={onClick}
    >
      <div className="tooltip" data-tip="Discard changes">
        <ArrowUturnLeftIcon className="size-5" />
      </div>
    </div>
  );
}

function ConfirmSaveChangesModalContent({ jobs }: { jobs: JobDoc[] }) {
  const { operationsRecordList } = useEditSchedulerContext();
  return (
    <>
      <AssignmentsConflictsPanel />
      <h3>{'Confirm and Save all the changes made over Job Assignments'}</h3>
      <div>
        <p>{'The following changes are yet to be saved:'}</p>
        <ul className="max-h-96 overflow-y-scroll">
          {operationsRecordList.map((operation, idx) => {
            const { groupIndex, jobAssignmentDoc } = operation;
            if (!jobAssignmentDoc) return;
            const operatorName = `${jobAssignmentDoc.get('receiver.company.name')}, ${jobAssignmentDoc.get(
              'receiver.user.nickname'
            )}`;
            const jobName = find(jobs, ['id', operation.jobId])?.get('name');
            const originalGroup = jobAssignmentDoc.get('groupIndex');
            return (
              <li
                key={`${operation.jobId}-${jobAssignmentDoc.id}`}
                className="text-xs"
              >
                {`"${operatorName}" is being `}
                <span className="font-bold">
                  {operation.type === 'assign'
                    ? 'assigned'
                    : operation.type === 'remove'
                      ? 'removed'
                      : operation.type === 'move' && 'moved'}
                </span>
                {[
                  operation.type === 'assign' && groupIndex !== undefined
                    ? ` to Group ${groupIndex + 1} of`
                    : operation.type === 'remove'
                      ? ' from'
                      : operation.type === 'move' &&
                          groupIndex !== undefined &&
                          originalGroup !== undefined
                        ? ` from Group ${originalGroup + 1} to Group ${groupIndex + 1} at`
                        : '',
                  `job "${jobName}".`,
                ].join(' ')}
              </li>
            );
          })}
        </ul>
      </div>
    </>
  );
}

function ConfirmRevertChangesContent() {
  return (
    <div className="text-center">
      <h3>{'Discard all the changes made over Job Assignments'}</h3>
      <div>
        {
          'Are you sure to clear all the unsaved changes? This action cannot be undone.'
        }
      </div>
    </div>
  );
}

function SaveIcon({ fill = 'fill-base-100', size = 15 }) {
  return (
    <svg
      fill="none"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 407.096 407.096"
      xmlSpace="preserve"
    >
      <g>
        <g>
          <path
            className={clsx(fill)}
            d="M402.115,84.008L323.088,4.981C319.899,1.792,315.574,0,311.063,0H17.005C7.613,0,0,7.614,0,17.005v373.086
			c0,9.392,7.613,17.005,17.005,17.005h373.086c9.392,0,17.005-7.613,17.005-17.005V96.032
			C407.096,91.523,405.305,87.197,402.115,84.008z M300.664,163.567H67.129V38.862h233.535V163.567z"
          />
          <path
            className={clsx(fill)}
            d="M214.051,148.16h43.08c3.131,0,5.668-2.538,5.668-5.669V59.584c0-3.13-2.537-5.668-5.668-5.668h-43.08
			c-3.131,0-5.668,2.538-5.668,5.668v82.907C208.383,145.622,210.92,148.16,214.051,148.16z"
          />
        </g>
      </g>
    </svg>
  );
}
